import { isPrairiTeamParticipant } from '../constants/prairiTeamWelcomeMessage';

const WELCOME_VIEWED_KEY = (userId: string) => `prairi.welcome.3.1.viewed:${userId}`;
const LAST_INBOX_CONVERSATION_KEY = (userId: string) => `prairi.inbox.lastOpen:${userId}`;

export function readLastInboxConversationId(userId?: string | null): string | null {
  if (!userId) return null;
  try {
    const value = window.localStorage.getItem(LAST_INBOX_CONVERSATION_KEY(userId));
    return value && value.trim() ? value : null;
  } catch {
    return null;
  }
}

export function persistLastInboxConversationId(
  userId?: string | null,
  conversationId?: string | null
): void {
  if (!userId || !conversationId) return;
  try {
    window.localStorage.setItem(LAST_INBOX_CONVERSATION_KEY(userId), conversationId);
  } catch {
    // Private mode / blocked storage — this visit still keeps the open thread in memory.
  }
}

export function hasViewedPrairi31Welcome(userId?: string | null): boolean {
  if (!userId) return false;
  try {
    return window.localStorage.getItem(WELCOME_VIEWED_KEY(userId)) === '1';
  } catch {
    return false;
  }
}

export function persistPrairi31WelcomeViewed(userId?: string | null): void {
  if (!userId) return;
  try {
    window.localStorage.setItem(WELCOME_VIEWED_KEY(userId), '1');
  } catch {
    // Private mode / blocked storage — badge will clear for this session only.
  }
}

export function conversationListUnreadCount(
  item: {
    unreadCount?: number;
    otherUser?: { name?: string | null; companyName?: string | null };
  },
  options: { isInvestor: boolean; welcomeViewed: boolean; showPrairiWelcome?: boolean }
): number {
  const unread = Number(item.unreadCount || 0);
  if (
    options.showPrairiWelcome === true &&
    options.isInvestor &&
    !options.welcomeViewed &&
    isPrairiTeamParticipant(item.otherUser || {}) &&
    unread === 0
  ) {
    return 1;
  }
  return unread;
}

/** Keep an opened thread at 0 across a stale inbox refresh, but not after a newer message. */
export function applyLocallyViewedUnread<
  T extends {
    conversationId: string;
    unreadCount?: number;
    lastMessageAt?: string | null;
  },
>(conversations: T[], viewed: Map<string, { lastMessageAt: string | null }>): T[] {
  if (viewed.size === 0) return conversations;
  return conversations.map(item => {
    const snap = viewed.get(item.conversationId);
    if (!snap) return item;
    if ((item.lastMessageAt ?? null) !== snap.lastMessageAt) return item;
    if (Number(item.unreadCount || 0) === 0) return item;
    return { ...item, unreadCount: 0 };
  });
}

export function unreadMessagesBadgeCount(
  conversations: Array<{
    conversationId?: string;
    unreadCount?: number;
    otherUser?: { name?: string | null; companyName?: string | null };
  }>,
  options: {
    isInvestor: boolean;
    welcomeViewed: boolean;
    showPrairiWelcome?: boolean;
    openConversationId?: string | null;
  }
): number {
  const threadUnread = conversations.reduce((sum, item) => {
    if (options.openConversationId && item.conversationId === options.openConversationId) {
      return sum;
    }
    return sum + Number(item.unreadCount || 0);
  }, 0);
  const showWelcome = options.showPrairiWelcome === true && options.isInvestor;
  const welcomeUnread = conversations.some(
    item =>
      showWelcome &&
      !options.welcomeViewed &&
      isPrairiTeamParticipant(item.otherUser || {}) &&
      Number(item.unreadCount || 0) > 0
  )
    ? 0
    : showWelcome && !options.welcomeViewed
      ? 1
      : 0;
  return threadUnread + welcomeUnread;
}
